import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  View,
  StyleSheet,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
} from "react-native";
import type { FC } from "react";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { getTransactionDetails } from "@services/api";
// eslint-disable-next-line import/no-named-as-default
import t from "@constants/localization";
import ProfileSmall from "@assets/svg/LedgerTxns/ProfileSmall";
import { fontFamily } from "@utils/styles";
import Text from "@components/CustomText";
import color from "@constants/color";

const PATH = "DebitCreditTransactions/ItemDetailsModal";

interface ItemDetailsModalProps {
  itemDetailsModalVisible: boolean;
  setItemDetailsModalVisible: (visible: boolean) => void;
  navigateToOrderDetails: (order_id: string) => void;
  deliveryTxnId: number;
  type: string;
}

interface LedgerItem {
  product_image: string;
  short_name: string;
  amount_txt: string;
  quantity: number;
  user_name: string;
  timestamp_txt: string;
  order_id: string;
}

interface ItemDetails {
  items: LedgerItem[];
  amount_txt?: string;
  quantity?: number;
  item_count?: number;
}

interface CrateDetails {
  crates: CrateItem[];
  amount_txt?: string;
  quantity?: number;
  item_count?: number;
}

interface CrateItem {
  crate_id: string;
  timestamp: string;
  timestamp_txt: string;
}

interface CrateAndItems {
  item_details: ItemDetails;
  crate_details: CrateDetails;
}
const defaultValue = {
  item_details: {} as ItemDetails,
  crate_details: {} as CrateDetails,
};
const ItemDetailsModal: FC<ItemDetailsModalProps> = ({
  itemDetailsModalVisible,
  setItemDetailsModalVisible,
  deliveryTxnId: delivery_txn_id,
  type,
  navigateToOrderDetails,
}) => {
  const [itemAndCrateDetails, setItemAndCrateDetails] =
    useState<CrateAndItems>(defaultValue);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"ITEM" | "CRATE">("ITEM");
  const mountedRef = useRef(false);
  const insets = useSafeAreaInsets();

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (itemDetailsModalVisible && delivery_txn_id && type) {
      getItemDetails();
    } else {
      setItemAndCrateDetails(defaultValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemDetailsModalVisible, delivery_txn_id, type]);

  const getItemDetails = async () => {
    try {
      setIsLoading(true);
      const response = await getTransactionDetails(delivery_txn_id, type);

      if (mountedRef.current) {
        setItemAndCrateDetails(response.data || defaultValue);
      }
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  const renderHeader = () => (
    <View style={styles.header}>
      <Text variant="title_18_semi">{t(`${PATH}/title`)}</Text>
      <TouchableOpacity
        onPress={() => setItemDetailsModalVisible(false)}
        style={styles.closeButton}
      >
        <Text variant="subtitle_20" style={styles.closeButtonText}>
          ✕
        </Text>
      </TouchableOpacity>
    </View>
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <View style={styles.loaderContainer}>
          <ActivityIndicator size="large" color="#666" />
        </View>
      );
    }

    return (
      <>
        {renderTabs()}
        {renderSummary(
          activeTab === "ITEM"
            ? itemAndCrateDetails.item_details
            : itemAndCrateDetails.crate_details,
        )}
        {activeTab === "ITEM" ? renderItems() : renderCrates()}
      </>
    );
  };

  const renderTabs = () => (
    <View style={styles.tabContainer}>
      <TouchableOpacity
        style={[styles.tab, activeTab === "ITEM" && styles.activeTab]}
        onPress={() => setActiveTab("ITEM")}
      >
        <Text
          style={[styles.tabText, activeTab === "ITEM" && styles.activeTabText]}
        >
          {t(`${PATH}/item`)}
        </Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.tab, activeTab === "CRATE" && styles.activeTab]}
        onPress={() => setActiveTab("CRATE")}
      >
        <Text
          style={[
            styles.tabText,
            activeTab === "CRATE" && styles.activeTabText,
          ]}
        >
          {t(`${PATH}/crate`)}
        </Text>
      </TouchableOpacity>
    </View>
  );

  const renderSummary = (item_details: ItemDetails | CrateDetails) => {
    return (
      <View style={styles.summaryContainer}>
        {item_details?.amount_txt ? (
          <View style={styles.summaryItem}>
            <Text variant="body_text_1" style={styles.summaryLabel}>
              {t(`${PATH}/amount`)}:
            </Text>
            <Text variant="title_18" style={styles.summaryValue}>
              {item_details.amount_txt}
            </Text>
          </View>
        ) : null}
        {item_details?.quantity ? (
          <View style={styles.summaryItem}>
            <Text variant="body_text_1" style={styles.summaryLabel}>
              {t(`${PATH}/quantity`)}:
            </Text>
            <Text variant="title_18" style={styles.summaryValue}>
              {item_details.quantity}
            </Text>
          </View>
        ) : null}
        {item_details?.item_count ? (
          <View style={styles.summaryItem}>
            <Text variant="body_text_1" style={styles.summaryLabel}>
              {t(`${PATH}/item`)}:
            </Text>
            <Text variant="title_18" style={styles.summaryValue}>
              {item_details.item_count}
            </Text>
          </View>
        ) : null}
      </View>
    );
  };

  const renderItems = () => (
    <ScrollView style={styles.itemList}>
      {itemAndCrateDetails.item_details?.items?.map((item, index) => (
        <View key={index} style={styles.itemContainer}>
          <View style={styles.productRow}>
            <Image
              source={{ uri: item.product_image }}
              style={styles.productImage}
            />
            <View style={styles.productDetails}>
              <Text variant="subtitle_16" style={styles.productName}>
                {item.short_name || ""}
              </Text>
              <View style={styles.priceRow}>
                <Text variant="title_16" style={styles.price}>
                  {item.amount_txt || ""}
                </Text>
                <Text variant="body_text_3" style={styles.quantity}>
                  Qty:
                  <Text variant="body_text_2" style={styles.black}>
                    {" "}
                    {item.quantity || ""}
                  </Text>
                </Text>
              </View>
              <TouchableOpacity
                onPress={() => navigateToOrderDetails(item.order_id)}
              >
                <Text variant="caption_2" style={styles.orderId}>
                  <Text style={styles.bold}>{t(`${PATH}/orderId`)} : </Text>
                  <Text>{item.order_id || ""}</Text>
                </Text>
              </TouchableOpacity>
            </View>
          </View>
          <View style={styles.userInfo}>
            <View style={styles.userNameContainer}>
              <ProfileSmall width={14} height={14} />
              <Text variant="caption_1" style={styles.userName}>
                {item.user_name}
              </Text>
            </View>
            <Text variant="caption_2" style={styles.penaltyDate}>
              <Text variant="caption_1" style={styles.debitDate}>
                {item.timestamp_txt}
              </Text>
            </Text>
          </View>
        </View>
      ))}
    </ScrollView>
  );

  const renderCrates = () => {
    const { crate_details } = itemAndCrateDetails;
    if (!crate_details?.crates?.length) {
      return (
        <View style={styles.emptyContainer}>
          <Text variant="body_text_1">{t(`${PATH}/noCrates`)}</Text>
        </View>
      );
    }

    return (
      <ScrollView style={styles.itemsContainer}>
        {crate_details.crates.map((crate, index) => (
          <View key={`${crate.crate_id}-${index}`} style={styles.itemCard}>
            <View style={styles.itemDetails}>
              <Text variant="body_text_3" style={styles.itemName}>
                {crate.crate_id}
              </Text>
              <Text variant="body_text_3" style={styles.penaltyDate}>
                <Text variant="body_text_2" style={styles.debitDate}>
                  {crate.timestamp_txt}
                </Text>
              </Text>
            </View>
          </View>
        ))}
      </ScrollView>
    );
  };

  return (
    <Modal
      animationType="slide"
      transparent
      visible={itemDetailsModalVisible}
      onRequestClose={() => setItemDetailsModalVisible(false)}
      statusBarTranslucent
      navigationBarTranslucent
    >
      <View style={styles.centeredView}>
        <TouchableOpacity
          style={styles.modalOverlay}
          onPress={() => setItemDetailsModalVisible(false)}
        />
        <View style={[styles.modalView, { paddingBottom: insets.bottom }]}>
          {renderHeader()}
          {renderContent()}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  centeredView: {
    flex: 1,
    backgroundColor: color.mediumBlacklightBlack,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "flex-end",
  },
  modalView: {
    backgroundColor: color.white,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    height: "70%",
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 40,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
  },
  closeButton: {
    padding: 8,
  },
  closeButtonText: {
    color: color.dimGray,
  },
  summaryContainer: {
    flexDirection: "row",
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
  summaryItem: {
    flexDirection: "row",
    paddingHorizontal: 8,
    backgroundColor: color.lightGray,
    marginRight: 8,
    borderRadius: 8,
    justifyContent: "center",
    alignItems: "center",
  },
  summaryLabel: {
    color: color.black,
    marginHorizontal: 8,
    lineHeight: 28,
  },
  summaryValue: {
    color: color.black,
    lineHeight: 38,
  },
  itemList: {
    maxHeight: "60%",
    backgroundColor: color.white,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 16,
  },
  itemContainer: {
    backgroundColor: color.lightGray,
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
  },
  productRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  productImage: {
    width: 60,
    height: 60,
    borderRadius: 8,
    marginRight: 12,
  },
  productDetails: {
    flex: 1,
  },
  productName: {
    color: color.darkGrayBlue,
    marginBottom: 4,
  },
  priceRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  price: {
    marginRight: 8,
  },
  quantity: {
    color: color.grayishBlue,
  },
  black: {
    color: color.black,
  },
  userInfo: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 8,
  },
  userNameContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  userName: {
    color: color.dimGray,
    marginLeft: 4,
    lineHeight: 20,
    fontWeight: "700",
  },
  penaltyDate: {
    color: color.grayishBlue,
    marginBottom: 2,
  },
  debitDate: {
    color: color.grayishBlue,
    marginBottom: 2,
    fontWeight: "700",
  },
  orderId: {
    color: color.royalBlue,
    borderBottomWidth: 1,
    borderColor: color.royalBlue,
    alignSelf: "flex-start",
  },
  bold: {
    fontFamily: fontFamily.bold,
  },
  tabContainer: {
    flexDirection: "row",
    marginBottom: 16,
    borderRadius: 8,
    padding: 4,
  },
  tab: {
    flex: 1,
    paddingVertical: 8,
    alignItems: "center",
    borderRadius: 30,
    borderWidth: 1,
    borderColor: color.lightGray2,
    marginHorizontal: 16,
  },
  activeTab: {
    backgroundColor: color.mediumBlack,
  },
  tabText: {
    fontFamily: fontFamily.medium,
    fontSize: 14,
    color: color.black,
  },
  activeTabText: {
    color: color.white,
  },
  emptyContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  itemsContainer: {
    height: "70%",
    backgroundColor: color.white,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 16,
  },
  itemCard: {
    backgroundColor: color.lightGray,
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
  },
  itemDetails: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  itemName: {
    color: color.black,
    fontSize: 16,
  },
});

export default ItemDetailsModal;
